import { getUserCountryCode } from 'Helper/getUserCountryCode';
import UADetector from 'Helper/UADetector';
import store from '_services/Redux/store';
import { selectCountryCode } from '_services/Redux/User/Selector/userSelectors';

const ASSIGNMENT_PREFIX = 'ab_assignment';
const EXPOSURE_PREFIX = 'ab_exposure';
const SUBJECT_SOURCE_PREFIX = 'ab_subject_source';
const ANON_ID_KEY = 'anon_id';
const IDENTITY_LINK_PREFIX = 'ab_identity_linked';

export type ABSubjectSource = 'anon' | 'user';

/**
 * Safely access sessionStorage if available.
 */
export const getSessionStorage = (): Storage | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

/**
 * Safely access localStorage if available.
 */
export const getLocalStorage = (): Storage | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

export const getAnonSubjectKey = (anon_id: string): string => `anon:${anon_id}`;

export const getUserSubjectKey = (user_id: number): string => `user:${user_id}`;

const toSubjectKey = (subject_key: string | number): string =>
    typeof subject_key === 'number'
        ? getUserSubjectKey(subject_key)
        : subject_key;

/**
 * Build assignment storage key for a user/experiment/salt.
 */
export const getAssignmentKey = (
    subject_key: string | number,
    experiment_id: string,
    salt: string,
): string =>
    `${ASSIGNMENT_PREFIX}:${toSubjectKey(
        subject_key,
    )}:${experiment_id}:${salt}`;

/**
 * Build exposure storage key for an experiment/salt, optionally scoped by user.
 */
export const getExposureKey = (
    experiment_id: string,
    salt: string,
    subject_key?: string | number,
): string =>
    subject_key === undefined
        ? `${EXPOSURE_PREFIX}:${experiment_id}:${salt}`
        : `${EXPOSURE_PREFIX}:${toSubjectKey(
              subject_key,
          )}:${experiment_id}:${salt}`;

export const getSubjectSourceLockKey = (
    experiment_id: string,
    salt: string,
): string => `${SUBJECT_SOURCE_PREFIX}:${experiment_id}:${salt}`;

/**
 * Read a boolean flag from storage.
 */
export const isStored = (storage: Storage | null, key: string): boolean => {
    if (!storage) {
        return false;
    }
    return storage.getItem(key) === '1';
};

/**
 * Persist a boolean flag in storage.
 */
export const storeFlag = (storage: Storage | null, key: string): void => {
    storage?.setItem(key, '1');
};

export const removeFlag = (storage: Storage | null, key: string): void => {
    storage?.removeItem(key);
};

/**
 * Generate a UUID v4 string.
 */
export const generateUuidV4 = (): string => {
    if (crypto.randomUUID) {
        return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, '0'),
    ).join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
        12,
        16,
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

let inMemoryAnonId: string | null = null;

export const getOrCreateAnonId = (): string => {
    const storage = getLocalStorage();
    const existing = storage?.getItem(ANON_ID_KEY) ?? inMemoryAnonId;

    if (existing) {
        inMemoryAnonId = existing;
        return existing;
    }

    const nextAnonId = generateUuidV4();
    inMemoryAnonId = nextAnonId;
    storage?.setItem(ANON_ID_KEY, nextAnonId);
    return nextAnonId;
};

export const getIdentityLinkKey = (anon_id: string, user_id: number): string =>
    `${IDENTITY_LINK_PREFIX}:${anon_id}:${user_id}`;

const normalizeCountryCode = (
    countryCode?: string | null,
): string | undefined => {
    if (typeof countryCode !== 'string') {
        return undefined;
    }

    const normalizedCountryCode = countryCode.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(normalizedCountryCode)
        ? normalizedCountryCode
        : undefined;
};

export const getABCountryCode = (): string | undefined => {
    const stateCountryCode = normalizeCountryCode(
        selectCountryCode(store.getState()),
    );
    if (stateCountryCode) {
        return stateCountryCode;
    }

    return normalizeCountryCode(getUserCountryCode());
};

/**
 * Resolve AB log source based on wrapper + device.
 */
export const getABSource = (): 'web' | 'ios' | 'android' => {
    const { isAndroid, isIPhone, isIPad } = UADetector();

    if (isAndroid) {
        return 'android';
    }

    if (isIPhone || isIPad) {
        return 'ios';
    }

    return 'web';
};
